import { z } from 'zod';

// ---- M8: diplomacy, treaties, alliances, trade ----

export const TreatyTypeSchema = z.enum(['non_aggression', 'alliance', 'trade']);
export type TreatyType = z.infer<typeof TreatyTypeSchema>;

/** A proposal is either a treaty or a peace offer that ends a hostile relation. */
export const ProposalTypeSchema = z.enum(['non_aggression', 'alliance', 'trade', 'peace']);
export type ProposalType = z.infer<typeof ProposalTypeSchema>;

export const NpcPersonalitySchema = z.enum(['expansionist', 'militarist', 'trader']);
export type NpcPersonalityName = z.infer<typeof NpcPersonalitySchema>;

export const TreatyViewSchema = z.object({
  type: TreatyTypeSchema,
  since: z.string().datetime(),
  /** Set once either side gave notice: the treaty still holds until then. */
  endsAt: z.string().datetime().nullable(),
});
export type TreatyView = z.infer<typeof TreatyViewSchema>;

export const ProposalViewSchema = z.object({
  id: z.string().uuid(),
  fromCivId: z.string().uuid(),
  fromCivName: z.string(),
  toCivId: z.string().uuid(),
  toCivName: z.string(),
  type: ProposalTypeSchema,
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});
export type ProposalView = z.infer<typeof ProposalViewSchema>;

export const RelationViewSchema = z.object({
  civId: z.string().uuid(),
  civName: z.string(),
  civColor: z.string(),
  status: z.enum(['neutral', 'hostile']),
  /** Who declared the hostility (true = this player): only the declarer can withdraw it alone. */
  hostileDeclaredByMe: z.boolean(),
  isNpc: z.boolean(),
  personality: NpcPersonalitySchema.nullable(),
  /** Newcomer protection end (null = not protected). */
  protectedUntil: z.string().datetime().nullable(),
  treaties: z.array(TreatyViewSchema),
});
export type RelationView = z.infer<typeof RelationViewSchema>;

export const DiplomacyOverviewSchema = z.object({
  relations: z.array(RelationViewSchema),
  incoming: z.array(ProposalViewSchema),
  outgoing: z.array(ProposalViewSchema),
  myProtectedUntil: z.string().datetime().nullable(),
  /** Current per-hour income from trade agreements. */
  tradeIncomePerHour: z.object({ metal: z.number().int(), energy: z.number().int() }),
  /** What can still be sent today (rolling 24 h) to treaty partners. */
  transferAllowance: z.object({ metal: z.number().int(), energy: z.number().int() }),
});
export type DiplomacyOverview = z.infer<typeof DiplomacyOverviewSchema>;

export const CreateProposalSchema = z.object({
  targetCivId: z.string().uuid(),
  type: ProposalTypeSchema,
  idempotencyKey: z.string().min(1).max(128),
});
export type CreateProposal = z.infer<typeof CreateProposalSchema>;

export const ResourceTransferSchema = z.object({
  targetCivId: z.string().uuid(),
  metal: z.number().int().min(0).max(100_000),
  energy: z.number().int().min(0).max(100_000),
  idempotencyKey: z.string().min(1).max(128),
}).refine(v => v.metal + v.energy > 0, { message: 'Adj meg küldendő mennyiséget' });
export type ResourceTransfer = z.infer<typeof ResourceTransferSchema>;

export const DIPLOMACY_RULES = {
  /** A proposal lapses after this long without an answer. */
  proposalTtlMs: 24 * 3_600_000,
  /** Notice period: a cancelled non-aggression pact or alliance holds this much longer. */
  treatyNoticeMs: 10 * 60_000,
  /** Income per active trade agreement (both partners receive it). */
  tradeIncomePerHour: { metal: 60, energy: 30 },
  maxTradeAgreements: 3,
  /** Rolling 24 h limit on resources sent to others (anti-abuse for multi-accounts). */
  dailyTransferLimit: { metal: 3000, energy: 3000 },
} as const;
